import React from 'react';
import {
  Box, Typography, Paper, Grid, Chip, List, ListItem, ListItemText, ListItemIcon,
  Card, CardContent, Alert, LinearProgress
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Psychology as PsychologyIcon,
  EmojiEmotions as MoodIcon,
  SelfImprovement as ConfidenceIcon,
  Spa as StressIcon,
  Visibility as FocusIcon,
  Bedtime as SleepIcon
} from '@mui/icons-material';

const DetailedPsychologyReport = ({ analysis }) => {
  if (!analysis || !analysis.detailedAnalysis) {
    return (
      <Alert severity="info">
        Complete your daily psychology assessment to view detailed analysis
      </Alert>
    );
  }

  const { detailedAnalysis, psychologicalProfile } = analysis;

  const getFactorIcon = (factor) => {
    const icons = {
      mood: <MoodIcon />,
      confidence: <ConfidenceIcon />,
      stress: <StressIcon />,
      focus: <FocusIcon />,
      sleep: <SleepIcon />
    };
    return icons[factor] || <PsychologyIcon />;
  };

  const getFactorColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'primary';
    if (score >= 40) return 'warning';
    return 'error';
  };

  const getFactorLevel = (score) => {
    if (score >= 80) return 'EXCELLENT';
    if (score >= 60) return 'GOOD';
    if (score >= 40) return 'MODERATE';
    return 'POOR';
  };

  const formatFactorName = (factor) => {
    return factor.charAt(0).toUpperCase() + factor.slice(1);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PsychologyIcon color="primary" />
        Pre-Trade Psychology Analysis
      </Typography>

      {/* Overall Score */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Overall Psychology Score
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h3" color="primary">
                {analysis.psychologyScore}/100
              </Typography>
              <Chip 
                label={analysis.riskLevel} 
                color={analysis.riskLevel === 'LOW' ? 'success' : analysis.riskLevel === 'MEDIUM' ? 'warning' : 'error'}
                variant="outlined"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Trading Recommendation
            </Typography>
            <Alert severity={analysis.shouldTrade ? 'success' : 'error'} sx={{ mb: 2 }}>
              {analysis.tradingAdvice}
            </Alert>
          </Grid>
        </Grid>
      </Paper>

      {/* Detailed Factor Analysis */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Psychological Factor Breakdown
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(detailedAnalysis).map(([factor, data]) => (
          <Grid item xs={12} md={6} lg={4} key={factor}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {getFactorIcon(factor)}
                  <Typography variant="h6">
                    {formatFactorName(factor)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h4" color={`${getFactorColor(data.score)}.main`}>
                    {data.score}
                  </Typography>
                  <Chip 
                    label={getFactorLevel(data.score)} 
                    color={getFactorColor(data.score)}
                    size="small"
                  />
                </Box>

                <LinearProgress 
                  variant="determinate" 
                  value={data.score} 
                  color={getFactorColor(data.score)}
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {data.description}
                </Typography>

                {data.concerns.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {data.concerns[0]}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Psychological Profile */}
      <Grid container spacing={3}>
        {/* Strengths */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
              <TrendingUpIcon />
              Psychological Strengths
            </Typography>
            <List dense>
              {psychologicalProfile.strengths.slice(0, 5).map((strength, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={strength} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Weaknesses */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
              <TrendingDownIcon />
              Areas for Improvement
            </Typography>
            <List dense>
              {psychologicalProfile.weaknesses.slice(0, 5).map((weakness, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <WarningIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText primary={weakness} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Risk Factors & Recommendations */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Risk Factors */}
        {psychologicalProfile.riskFactors.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'error.main' }}>
                ⚠️ Risk Factors
              </Typography>
              <List dense>
                {psychologicalProfile.riskFactors.map((risk, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <WarningIcon color="error" />
                    </ListItemIcon>
                    <ListItemText primary={risk} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Recommendations */}
        {psychologicalProfile.recommendations.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'info.main' }}>
                💡 Recommendations
              </Typography>
              <List dense>
                {psychologicalProfile.recommendations.map((rec, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckIcon color="info" />
                    </ListItemIcon>
                    <ListItemText primary={rec} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Warnings */}
      {analysis.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            ⚠️ Important Warnings
          </Typography>
          <List dense>
            {analysis.warnings.map((warning, index) => (
              <ListItem key={index}>
                <ListItemText primary={warning} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
    </Box>
  );
};

export default DetailedPsychologyReport;